import React from 'react';

/**
 * FacetRail — left-rail faceted filter sidebar.
 *
 * Used on every inventory page (Agentlets, Workflows, Connectors,
 * Discovery, Audit, Compliance) to filter the primary table. Each
 * group is a category of related facet options; each option has a
 * label, count (optional), and checked state.
 *
 * Selection is *controlled* — the parent owns the selected facets
 * (typically a `{ groupKey: Set<optionKey> }` map) and calls
 * `onToggle(groupKey, optionKey, nextChecked)`.
 *
 * @param {Object} props
 * @param {Array<{
 *   key: string,
 *   label: string,
 *   options: Array<{ key: string, label: string, count?: number, checked?: boolean }>,
 * }>} props.groups
 * @param {(groupKey: string, optionKey: string, nextChecked: boolean) => void} props.onToggle
 * @param {string} [props.className]
 * @param {string} [props.ariaLabel='Filters']
 */
export function FacetRail({ groups, onToggle, className = '', ariaLabel = 'Filters' }) {
  /* G174 — Sticky to the viewport top with bounded height + internal scroll.
   *   - Without this, a rail with many groups (e.g. Connectors: ~18 categories
   *     + ~37 vendors) pushes the grid container ~2000px tall while the
   *     filtered table column is only ~300px, leaving huge whitespace
   *     beneath the rows.
   *   - With sticky + max-height = (viewport - top offset), the rail tracks
   *     the viewport and the grid container is bounded by the rail's height
   *     OR the table's height (whichever is shorter), so toggling facets no
   *     longer creates a "broken" empty-page look.
   *   - Internal overflow-y:auto means the rail scrolls within itself when
   *     the option list is taller than the viewport. */
  return (
    <aside
      role="region"
      aria-label={ariaLabel}
      className={
        'bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm ' +
        'p-4 self-start sticky top-4 ' + className
      }
      style={{
        maxHeight: 'calc(100vh - 2rem)',
        overflowY: 'auto',
        // Pin overflow-x explicitly. With only overflowY set, CSS promotes
        // overflow-x from `visible` to `auto`, so any sub-pixel-wide option
        // row spawns a horizontal scrollbar and clips labels on the left.
        // Hidden keeps the rail at its column width and lets the per-option
        // `truncate` show an ellipsis for long labels (e.g. "Identity & Access
        // Management") instead of scrolling.
        overflowX: 'hidden',
      }}
    >
      {groups.map((group, gi) => (
        <fieldset
          key={group.key}
          className={
            'border-0 m-0 p-0 ' +
            (gi < groups.length - 1 ? 'pb-4 mb-4 border-b border-v2-border-subtle' : '')
          }
        >
          <legend className="text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted p-0">
            {group.label}
          </legend>
          {/* R1.4 — Optional per-group hint, rendered below the legend in
           * smaller muted text. Used by the Agentlets inventory to clarify
           * scope-bound facets (e.g. "Archetype" only applies to governed
           * rows, "Risk tier" only applies to shadow rows). */}
          {group.hint && (
            <div className="text-[10px] text-v2-ink-muted mb-2 -mt-0.5 italic">{group.hint}</div>
          )}
          {!group.hint && <div className="mb-2" />}
          <div className="space-y-1">
            {group.options.map((opt) => (
              <label
                key={opt.key}
                className={
                  'flex items-center gap-2 text-[12px] cursor-pointer select-none ' +
                  'py-1 px-1 rounded transition-colors duration-v2 ' +
                  'hover:bg-v2-muted ' +
                  (opt.checked ? 'text-v2-ink' : 'text-v2-ink-secondary')
                }
              >
                <input
                  type="checkbox"
                  className="v2-focus-ring sr-only peer"
                  checked={!!opt.checked}
                  onChange={(e) => onToggle && onToggle(group.key, opt.key, e.target.checked)}
                />
                {/* Custom checkbox visual — keeps perfect alignment with our palette */}
                <span
                  aria-hidden="true"
                  className={
                    'w-3.5 h-3.5 rounded-[3px] border flex-shrink-0 ' +
                    'flex items-center justify-center transition-colors duration-v2 ' +
                    (opt.checked
                      ? 'bg-v2-action border-v2-action'
                      : 'bg-v2-surface border-v2-border-strong')
                  }
                >
                  {opt.checked && (
                    <svg viewBox="0 0 16 16" className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M3 8l3 3 7-7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </span>
                <span className="flex-1 min-w-0 truncate" title={opt.label}>{opt.label}</span>
                {opt.count != null && (
                  <span className="text-[10.5px] text-v2-ink-muted tabular-nums">{opt.count}</span>
                )}
              </label>
            ))}
          </div>
        </fieldset>
      ))}
    </aside>
  );
}

export default FacetRail;
